import { useSelector } from "react-redux";
import { ALL_FEATURE_TYPES_MAP } from "../../../data/user-features-constants";
import React, { useEffect, useRef, useState, lazy, Suspense } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CircularProgress, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  ACTIVITY_DETAIL_TABLE_HEADER_KEYS,
  ALLOWED_MIME_TYPES,
  INVENTORY_ACTIVITY_STATUS,
  INVENTORY_ACTIVITY_TYPES,
  IS_PAGE_RELOADED_KEY,
  POST_FINAL_STAGE_STATUS,
  ITEMS_PER_PAGE
} from "../data-handling/finance-inventory-constants";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from '@mui/icons-material/Close';
import useFinanceInventory from "../data-handling/use-finance-inventory";
import {
  DISCOUNT_TYPE_ENUMS,
  IActivityIndividualItem,
} from "../data-handling/finance-inventory-types";
import ActionCta from "../components/action-cta";
import PageMetaData from "../components/page-metadata";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import ItemsEditModal from "../components/items-edit-modal";
import moment from "moment";
import { DATE_TIME_FORMATS } from "../../../data/constants";
import { IActivityItem } from "../data-handling/finance-inventory-types";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { currency } from "../../../helpers/common-helper";

const ConfirmActionModal = lazy(() => import("../components/confirm-action-modal"));
const currencySymbol = currency();
const ActivityEditScreen = () => {
  const {
    activityDetail,
    selectedActivityRow,
    uploadAttachment,
    submitInventoryActivity,
    componentLoaders,
    currentActivityList,
    setSelectedActivityRow,
    activityType,
    onPageChange,
    pageNumber,
    submitBulkApproval,
    rejectInventoryActivity
  } = useFinanceInventory();

  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [openItemEditModal, setOpenItemEditModal] = useState<boolean>(false);
  const [showEditSection, setShowEditSection] = useState(true)

  const userData = useSelector((state: any) => state.businessReducer.userData);
  const canEditPostFinalActivity = userData?.allowedFeatures?.includes(ALL_FEATURE_TYPES_MAP.EDIT_POST_FINAL_INVENTORY_ACTIVITY);
  const canApprovePostFinalActivity = userData?.allowedFeatures?.includes(ALL_FEATURE_TYPES_MAP.APPROVE_POST_FINAL_INVENTORY_ACTIVITY);
  const canRejectPostFinalActivity = userData?.allowedFeatures?.includes(ALL_FEATURE_TYPES_MAP.REJECT_POST_FINAL_INVENTORY_ACTIVITY);

  const [checkedItems, setCheckedItems] = useState<string[]>([]); 
  const [showApprovalConfirmModal, setShowApprovalConfirmModal] = useState(false);
  const [showRejectConfirmModal, setShowRejectConfirmModal] = useState(false);
  const isPostApprovedPreActivity = (activityDetail?.postFinalDate || activityDetail?.postFinalStage === POST_FINAL_STAGE_STATUS.approved) &&
    [
      INVENTORY_ACTIVITY_TYPES.goods_received_note,
      INVENTORY_ACTIVITY_TYPES.transfer_ins,
    ].includes(activityDetail?.activityType || "")

  const shouldHideEditAction = activityDetail?.postFinalStage===POST_FINAL_STAGE_STATUS.rejected || isPostApprovedPreActivity || [
      INVENTORY_ACTIVITY_TYPES.transfer_ins,
      INVENTORY_ACTIVITY_TYPES.post_final_transfer_ins,
    ].includes(activityDetail?.activityType || "") || !canEditPostFinalActivity;
  const shouldHideAttachmentAction = isPostApprovedPreActivity;
  const shouldShowApproveOption =
    (([
      INVENTORY_ACTIVITY_TYPES.post_final_transfer_ins,
      INVENTORY_ACTIVITY_TYPES.post_final_goods_received_note,
    ].includes(activityDetail?.activityType || "") &&
      activityDetail?.status === INVENTORY_ACTIVITY_STATUS.draft) ||
    ([
      INVENTORY_ACTIVITY_TYPES.goods_received_note,
      INVENTORY_ACTIVITY_TYPES.transfer_ins,
    ].includes(activityDetail?.activityType || "") &&
    (!activityDetail?.postFinalDate && !activityDetail?.postFinalStage))) && canApprovePostFinalActivity; //postFinalDate will be removed later
  const shouldShowRejectOption = ([
    INVENTORY_ACTIVITY_TYPES.goods_received_note,
    INVENTORY_ACTIVITY_TYPES.transfer_ins,
  ].includes(activityDetail?.activityType || "") &&
    (!activityDetail?.postFinalDate && !activityDetail?.postFinalStage)) && canRejectPostFinalActivity; //postFinalDate will be removed later

  const getTextFromSelectedValue = (selectedVal: string) => {
    switch (selectedVal) {
      case INVENTORY_ACTIVITY_TYPES.goods_received_note:
        return "All Goods Received Notes";
      case INVENTORY_ACTIVITY_TYPES.transfer_ins:
        return "All Transfer Ins";
      case INVENTORY_ACTIVITY_TYPES.post_final_goods_received_note:
        return "All Goods Received Notes - Approved";
      case INVENTORY_ACTIVITY_TYPES.post_final_transfer_ins:
        return "All Transfer Ins - Approved";
      default:
        return "--";
    }
  };

  useEffect(() => {
    window.onbeforeunload = () => {
      sessionStorage.setItem(IS_PAGE_RELOADED_KEY, IS_PAGE_RELOADED_KEY);
    };
  }, []);

  useEffect(() => {
    const shouldNavigateBack = sessionStorage.getItem(IS_PAGE_RELOADED_KEY); // if a reload flag is set
    if (shouldNavigateBack) {
      sessionStorage.removeItem(IS_PAGE_RELOADED_KEY);
      navigate(-1);
    }
  }, [navigate]);

  useEffect(() => {
    if (!currentActivityList?.length) return;

    const approvedIds = currentActivityList
      .filter((item) => !!item.postFinalDate)
      .map((item) => item.mid);

    if (approvedIds.length > 0) {
      setCheckedItems((prev) => prev.filter((id) => !approvedIds.includes(id)));
    }
  }, [currentActivityList]);

  useEffect(() => {
    setShowEditSection(true);
  }, [selectedActivityRow?.mid]);

  const getTableCell = (columnName: string, item: IActivityIndividualItem) => {
    switch (columnName) {
      case ACTIVITY_DETAIL_TABLE_HEADER_KEYS.name:
        return (
          <td className="p-10 pl-36">
            <div className="flex items-start flex-col">
              <div className="text-14 font-500">{item.itemName}</div>
              <div className="text-12 mt-5 text-grey6C">
                SKU: {item.skuCode}
              </div>
            </div>
          </td>
        );
      case ACTIVITY_DETAIL_TABLE_HEADER_KEYS.quantity:
        return (
          <td className="text-center">
            {item.itemQty}
            <span className="ml-8">{item.measuringUnit}</span>
          </td>
        );
      case ACTIVITY_DETAIL_TABLE_HEADER_KEYS.rate:
        return <td className="text-center">{currencySymbol}{item.unitCost}</td>;
      case ACTIVITY_DETAIL_TABLE_HEADER_KEYS.taxes:
        return <td className="text-center">{currencySymbol}{item.totalTax.toFixed(2)}</td>;
      case ACTIVITY_DETAIL_TABLE_HEADER_KEYS.amount:
        return <td className="text-center">{currencySymbol}{item.cost.toFixed(2)}</td>;
      default:
        return <></>;
    }
  };

  const handleFileChange = (e: any) => {
    const file = e.target.files?.[0];
    uploadAttachment(file);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, mid: string) => {
    if (e.target.checked) {
      setCheckedItems((prev) => [...prev, mid]);
    } else {
      setCheckedItems((prev) => prev.filter((id) => id !== mid));
    }
  };

  return (
    <div className="flex w-full h-screen overflow-hidden gap-16">
      <div className="relative w-[320px] min-w-[280px] bg-white rounded-[8px] p-16 shadow-sm border border-[#E8E8E9] overflow-y-auto">

        <div className="text-16 font-600 mb-12">
          <span>{getTextFromSelectedValue(activityType)}</span>
        </div>

        {!!checkedItems.length && (
          <div className="flex items-center justify-between mb-12 whitespace-nowrap">
            <ActionCta
              containerClass=""
              Image={DoneAllIcon}
              onClick={() => setShowApprovalConfirmModal(true)}
              text="Mark as Approved"
            />
            <span className="bg-greyF0 text-grey33 text-12 px-8 py-2 rounded-full w-fit">
              {checkedItems.length} Selected
            </span>
          </div>
        )}

        <div className="flex flex-col gap-12 pb-48">
          {currentActivityList?.map((item) => (
            <label
              key={item.orderNumber}
              className="flex items-start w-full cursor-pointer py-8 border-b border-greyEB"
            >
              <div className="flex-shrink-0">
                <input
                  type="checkbox"
                  className="accent-blue-600 mr-12 mt-[2px]"
                  checked={checkedItems.includes(item.mid)}
                  onChange={(e) => handleCheckboxChange(e, item.mid)}
                  defaultChecked={false}
                  disabled={!!item.postFinalDate || item.postFinalStage === POST_FINAL_STAGE_STATUS.approved  || item.postFinalStage === POST_FINAL_STAGE_STATUS.rejected}
                />
              </div>

              <div className="flex justify-between w-full">
                <div className="flex flex-col text-14">
                  <button
                    className={`font-semibold text-left hover:underline ${selectedActivityRow?.orderNumber === item.orderNumber
                      ? 'text-[#397EF5]'
                      : 'text-grey33'
                      }`}
                    onClick={() => {
                      setSelectedActivityRow(item);
                    }}
                  >
                    {item.orderNumber}
                  </button>
                  <span className="text-12 text-grey6C">
                    {moment(item.activityDate).format(DATE_TIME_FORMATS.dayMonthYearShort)} | {item.supplierName}
                  </span>
                </div>
                <div className="flex flex-col text-14 text-right">
                  <span className="text-12 font-600">{currencySymbol}{item.netCost}</span>
                  {(item.postFinalStage === POST_FINAL_STAGE_STATUS.approved || item.postFinalDate) && (
                    <span className="text-[12px] font-500 text-green-600">Approved</span>
                  )}
                  {item.postFinalStage === POST_FINAL_STAGE_STATUS.rejected && (
                    <span className="text-[12px] font-500 text-rede2574c">Rejected</span>
                  )}
                </div>
              </div>
            </label>
          ))}
        </div>

        <div className="bottom-0 bg-white flex justify-end items-center py-2 pr-20 border-t">
          <span className="text-[13px] mr-16 font-400">
            {`${pageNumber === 1 ? pageNumber : ITEMS_PER_PAGE * (pageNumber - 1)} - ${ITEMS_PER_PAGE * (pageNumber - 1) + ITEMS_PER_PAGE
              }`}
          </span>
          <IconButton onClick={() => onPageChange("prev")}>
            <KeyboardArrowLeftIcon className="h-24 w-24 text-[#898989]" />
          </IconButton>
          <IconButton onClick={() => onPageChange("next")}>
            <KeyboardArrowRightIcon className="h-24 w-24 text-[#898989]" />
          </IconButton>
        </div>
      </div>
      <div className="flex-1 bg-white rounded-[8px] pb-20 relative overflow-y-auto">

        {componentLoaders.GENERAL && (
          <div className="absolute top-0 right-0 left-0 bottom-0 bg-[#00000029] flex justify-center items-center">
            <CircularProgress />
          </div>
        )}
        <ItemsEditModal
          onClose={() => setOpenItemEditModal(false)}
          open={openItemEditModal}
        />
        <div className="flex items-center justify-between px-12 py-16">
          <div>
            <IconButton className="mr-8" onClick={() => navigate(-1)}>
              <ArrowBackIcon className="h-24 w-24" />
            </IconButton>
            <span className="text-18 font-600">
              Viewing{" "}
              {activityDetail?.orderNumber}
            </span>
          </div>
        </div>
        {showEditSection && <div className="border-t px-16 border-solid birder-[#E8E8E9]">
          {shouldShowApproveOption && (
            <ActionCta
              isLoading={componentLoaders.MARK_APPROVED}
              containerClass={""}
              Image={DoneAllIcon}
              onClick={() => submitInventoryActivity(() => setShowEditSection(true))}
              text="Mark as Approved"
            />
          )}
            {shouldShowRejectOption && (
            <ActionCta
              isLoading={componentLoaders.REJECTED}
              Image={CloseIcon}
              onClick={() => setShowRejectConfirmModal(true)}
              text="Reject"
            />
          )}
          <input
            type="file"
            ref={fileInputRef}
            accept={ALLOWED_MIME_TYPES.join(",")}
            onChange={handleFileChange}
            className="hidden"
          />
          {!shouldHideAttachmentAction && (
            <ActionCta
              isLoading={componentLoaders.UPLOAD}
              imageClass={"rotate-45"}
              Image={AttachFileIcon}
              onClick={() => {
                // @ts-ignore
                fileInputRef.current?.click();
              }}
              text="Add Attachment"
            />
          )}
          {!shouldHideEditAction && (
            <ActionCta
              isLoading={componentLoaders.EDITING}
              Image={CreateOutlinedIcon}
              onClick={() => setOpenItemEditModal(true)}
              text="Edit"
            />
          )}
        </div>}
        <PageMetaData />
        <table className="w-full border text-13">
          <thead>
            <tr className="bg-greyF9">
              {Object.keys(ACTIVITY_DETAIL_TABLE_HEADER_KEYS).map(
                (columnName) => {
                  return columnName ===
                    ACTIVITY_DETAIL_TABLE_HEADER_KEYS.name ? (
                    <th className="text-left p-10 font-500 text-xs text-[#7F7F7F] pl-36">
                      ITEMS & DESCRIPTION
                    </th>
                  ) : (
                    <th className="p-10 font-500 text-xs text-[#7F7F7F]">{(columnName || "").toUpperCase()}</th>
                  );
                }
              )}
            </tr>
          </thead>
          <tbody>
            {activityDetail?.items?.map((item) => (
              <tr key={item.skuCode} className="border-t border-greyEB">
                {Object.keys(ACTIVITY_DETAIL_TABLE_HEADER_KEYS).map(
                  (columnName) => {
                    return getTableCell(columnName, item);
                  }
                )}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-24 flex justify-center">
          <div className="border border-1 px-16 py-8 pb-16 w-[40%] min-w-[400px] rounded-[8px] border-solid border-[#E8E8E9]">
            {activityDetail?.itemTotalCost !== undefined && activityDetail?.itemTotalCost !== null && (
              <BillComponentsRow
                keyVal={"Subtotal"}
                value={`${currencySymbol}${activityDetail.itemTotalCost?.toFixed(2)}`}
              />
            )}
            {activityDetail?.itemTotalCost !== undefined && activityDetail?.itemTotalCost !== null && (
              <BillComponentsRow
                keyVal={"Total tax"}
                value={`${currencySymbol}${activityDetail.totalTax?.toFixed(2)}`}
              />
            )}
            {
              !!activityDetail?.discountSummary?.map((discount, idx) => (
                <BillComponentsRow
                  key={idx}
                  keyVal={discount.discountCode}
                  value={
                    discount.discountType === DISCOUNT_TYPE_ENUMS.ABSOLUTE
                      ? `${currencySymbol}${discount.discountRate}`
                      : `${discount.discountRate}%`
                  }
                />
              ))
            }
            {activityDetail?.itemNetCost !== undefined && activityDetail?.itemNetCost !== null && (
              <div className="flex justify-between text-xl font-600 text-16 mt-20">
                <span>Total</span>
                <span>{currencySymbol}{activityDetail.itemNetCost.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {showApprovalConfirmModal && (
        <Suspense fallback={null}>
          <ConfirmActionModal
            title="Confirm Bulk Approval"
            message="Do you want to perform approval for the selected items?"
            confirmLabel="Approve"
            cancelLabel="Cancel"
            onConfirm={() => {
              submitBulkApproval(checkedItems, () => setCheckedItems([]));
              setShowApprovalConfirmModal(false);
            }}
            onCancel={() => setShowApprovalConfirmModal(false)}
          />
        </Suspense>
      )}

      {showRejectConfirmModal && (
        <Suspense fallback={null}>
          <ConfirmActionModal
            title="Confirm Rejection"
            message="You are about to reject this action. Do you want to continue?"
            confirmLabel="Reject"
            cancelLabel="Cancel"
            onConfirm={() => {
              rejectInventoryActivity(() => setShowEditSection(true))
              setShowRejectConfirmModal(false);
            }}
            onCancel={() => setShowRejectConfirmModal(false)}
          />
        </Suspense>
      )}
    </div>
  );
};

function BillComponentsRow({
  keyVal,
  value,
}: {
  keyVal: string;
  value: string;
}) {
  return (
    <div className="flex justify-between !text-[14px] mt-8">
      <span>{keyVal}</span>
      <span>{value}</span>
    </div>
  );
}

export default ActivityEditScreen;
